import axios from 'axios';
import { useQuery } from '@tanstack/react-query';
import { filterUrls } from 'config/urls';
import type { SimulatorFilterCategory, SimulatorFilterOption } from './SimulatorFilterPanel';
import {
    buildSimulatorFilterAttributeNamesInclusion,
    buildSimulatorFilterShowOnlyPayload,
    SIMULATOR_FILTER_ATTRIBUTE_NAMES_LIMIT,
    SIMULATOR_FILTER_FEATURE_NAME,
    type SimulatorFilterScopeItem,
    type SimulatorShowOnlyState,
} from './simulatorFilter.helpers';

export type { SimulatorFilterScopeItem };

type AttributeNamesApiRow = {
    attribute_name?: string;
    is_active?: boolean;
};

type UseSimulatorFilterAttributeNamesArgs = {
    category: SimulatorFilterCategory | null;
    appliedScope?: SimulatorFilterScopeItem[];
    showOnly?: SimulatorShowOnlyState;
    enabled?: boolean;
};

function parseGlobalFilterAttributeNamesResponse(data: unknown): SimulatorFilterOption[] {
    const payload = data as { data?: AttributeNamesApiRow[] };
    if (!Array.isArray(payload?.data)) return [];

    return payload.data
        .map((item) => {
            const name = item.attribute_name?.trim();
            if (!name) return null;
            return { id: name, label: name };
        })
        .filter((option): option is SimulatorFilterOption => option !== null);
}

export function useSimulatorFilterAttributeNames({
    category,
    appliedScope,
    showOnly,
    enabled = true,
}: UseSimulatorFilterAttributeNamesArgs) {
    const attributeType = category?.attributeType ?? category?.key ?? '';
    const searchDimension = category?.dimension ?? 'dim1';
    const inclusionFilters = buildSimulatorFilterAttributeNamesInclusion(appliedScope, attributeType);
    const showOnlyPayload = showOnly ? buildSimulatorFilterShowOnlyPayload(showOnly) : undefined;
    const inclusionQueryKey = JSON.stringify(inclusionFilters);
    const showOnlyQueryKey = JSON.stringify(showOnlyPayload ?? []);

    const query = useQuery<SimulatorFilterOption[]>({
        queryKey: [
            'simulator-filter-attribute-names',
            searchDimension,
            attributeType,
            inclusionQueryKey,
            showOnlyQueryKey,
            SIMULATOR_FILTER_ATTRIBUTE_NAMES_LIMIT,
        ],
        enabled: enabled && Boolean(category) && Boolean(attributeType),
        queryFn: async () => {
            const { data } = await axios.post(filterUrls.getAttributeNamesUrl, {
                attributeType,
                email: localStorage.getItem('email'),
                featureName: SIMULATOR_FILTER_FEATURE_NAME,
                filters: {
                    inclusion: inclusionFilters,
                    exclusion: [],
                },
                ...(showOnlyPayload ? { show_only: showOnlyPayload } : {}),
                limit: SIMULATOR_FILTER_ATTRIBUTE_NAMES_LIMIT,
                offset: 0,
                searchDimension,
                searchKeyword: '',
            });

            return parseGlobalFilterAttributeNamesResponse(data);
        },
    });

    return {
        options: query.data ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
    };
}
